// SavingsPlanPanel.jsx - Savings Plans utilization/coverage + Unblended vs Amortized cost
// Dipakai di halaman AWS Cost Explorer & AWS Billing. Akun mengikuti localStorage 'awsinv.account'.
import React, { useEffect, useState } from 'react';

const API = (window.__AWS_API_BASE__ || '');
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const money = (n) => (n == null ? '–' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const pct = (n) => (n == null ? '–' : Number(n).toFixed(1) + '%');

function monthList() {
  const out = [];
  const d = new Date(); d.setUTCDate(1);
  for (let i = 0; i < 12; i++) { out.push({ y: d.getUTCFullYear(), m: d.getUTCMonth() + 1 }); d.setUTCMonth(d.getUTCMonth() - 1); }
  return out;
}
function tone(p) {
  if (p == null) return { fg: '#667085', bg: '#f2f4f7', label: 'No data' };
  if (p >= 95) return { fg: '#067647', bg: '#ecfdf3', label: 'Healthy' };
  if (p >= 80) return { fg: '#b54708', bg: '#fffaeb', label: 'Watch' };
  return { fg: '#b42318', bg: '#fef3f2', label: 'Under-used' };
}

const card = { background: '#fff', border: '1px solid #eaecf0', borderRadius: 12, padding: '14px 16px', minWidth: 0 };
const lbl = { fontSize: 11, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: '#667085' };
const big = { fontSize: 22, fontWeight: 700, color: '#101828', marginTop: 4, fontVariantNumeric: 'tabular-nums' };
const sub = { fontSize: 12, color: '#667085', marginTop: 4, lineHeight: 1.4 };

export default function SavingsPlanPanel({ embedded = false } = {}) {
  const list = monthList();
  const [sel, setSel] = useState(new Date().getUTCDate() <= 3 ? 1 : 0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [openRaw, setOpenRaw] = useState(() => { try { return localStorage.getItem('spPanel.open') === '1'; } catch (_) { return false; } });
  const open = embedded ? true : openRaw;
  const setOpen = (v) => { if (embedded) return; setOpenRaw(v); try { localStorage.setItem('spPanel.open', v ? '1' : '0'); } catch (_) {} };
  let acct = 'primary';
  try { acct = localStorage.getItem('awsinv.account') || 'primary'; } catch (_) {}

  const load = async (refresh) => {
    const { y, m } = list[sel];
    setLoading(true); setErr('');
    try {
      let token = '';
      try { token = localStorage.getItem('auth_token') || ''; } catch (_) {}
      const url = API + '/api/aws-sp/summary?account=' + encodeURIComponent(acct) + '&year=' + y + '&month=' + m + (refresh ? '&refresh=1' : '');
      const r = await fetch(url, { headers: { Authorization: 'Bearer ' + token, 'X-Aws-Account': acct } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
      setData(j);
    } catch (e) { setErr(e.message); setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(false); }, [sel]); // eslint-disable-line

  const u = data && data.utilization;
  const cv = data && data.coverage;
  const c = data && data.cost;
  const t = tone(u ? u.pct : null);
  const errs = data && data.errors ? Object.entries(data.errors).filter(([, v]) => v) : [];
  const upfrontInMonth = c && c.unblended - c.amortized > 1;

  return (
    <div style={{ background: '#f9fafb', border: '1px solid #eaecf0', borderRadius: 14, padding: open ? 16 : '10px 16px', margin: '0 0 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={() => setOpen(!open)} style={{ border: 0, background: 'none', cursor: 'pointer', padding: 0, fontSize: 15, fontWeight: 700, color: '#101828' }}>
          {embedded ? '' : (open ? '▾ ' : '▸ ')}Savings Plans &amp; Amortized Cost
        </button>
        {u && <span style={{ fontSize: 12, fontWeight: 600, color: t.fg, background: t.bg, borderRadius: 999, padding: '2px 10px' }}>{t.label}</span>}
        {!open && data && !data.empty && (
          <span style={{ fontSize: 12, color: '#667085' }}>
            {u ? 'SP ' + pct(u.pct) + ' used · saved ' + money(u.netSavings) : 'No Savings Plan'}
            {data.reservations && data.reservations.pct > 0 ? ' · RI ' + pct(data.reservations.pct) + ' used · saved ' + money(data.reservations.netSavings) : ''}
            {c ? ' · Amortized ' + money(c.amortized) : ''}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <select value={sel} onChange={(e) => setSel(Number(e.target.value))} style={{ border: '1px solid #d0d5dd', borderRadius: 8, padding: '5px 8px', fontSize: 13, background: '#fff' }}>
          {list.map((x, i) => <option key={i} value={i}>{MONTHS[x.m - 1]} {x.y}</option>)}
        </select>
        <button onClick={() => load(true)} disabled={loading} style={{ border: '1px solid #d0d5dd', background: '#fff', borderRadius: 8, padding: '5px 10px', fontSize: 13, cursor: 'pointer' }}>
          {loading ? 'Loading…' : '⟳ Refresh'}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 12 }}>
          {err && <div style={{ ...sub, color: '#b42318' }}>Failed to load: {err}</div>}
          {data && data.empty && <div style={sub}>No data yet for this month.</div>}
          {data && !data.empty && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
                <div style={card}>
                  <div style={lbl}>SP Utilization</div>
                  <div style={{ ...big, color: u ? t.fg : '#98a2b3' }}>{u ? pct(u.pct) : '–'}</div>
                  <div style={sub}>{u ? <>Used {money(u.used)} of {money(u.totalCommitment)}<br />Unused {money(u.unused)}</> : 'No active Savings Plan in this period'}</div>
                </div>
                <div style={card}>
                  <div style={lbl}>SP Coverage</div>
                  <div style={big}>{cv ? pct(cv.pct) : '–'}</div>
                  <div style={sub}>{cv ? <>Covered {money(cv.covered)}<br />Still On-Demand {money(cv.onDemand)}</> : '–'}</div>
                </div>
                <div style={card}>
                  <div style={lbl}>Net Savings</div>
                  <div style={{ ...big, color: u && u.netSavings > 0 ? '#067647' : '#101828' }}>{u ? money(u.netSavings) : '–'}</div>
                  <div style={sub}>{u ? <>On-Demand equivalent {money(u.onDemandEquivalent)}<br />SP cost {money(u.amortizedUpfront + u.amortizedRecurring)}</> : '–'}</div>
                </div>
                <div style={card}>
                  <div style={lbl}>Unblended vs Amortized</div>
                  <div style={big}>{c ? money(c.amortized) : '–'}</div>
                  <div style={sub}>{c ? <>Amortized (true monthly cost)<br />Unblended (cash) {money(c.unblended)}</> : '–'}</div>
                </div>
              </div>
              {u && data.daily && data.daily.length > 0 && (
                <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 34, marginTop: 12 }} title="Daily SP utilization">
                  {data.daily.map((d) => (
                    <div key={d.date} title={d.date + ': ' + pct(d.pct) + ', saved ' + money(d.savings)}
                      style={{ flex: 1, height: Math.max(3, (d.pct / 100) * 34), background: tone(d.pct).fg, opacity: 0.75, borderRadius: 2 }} />
                  ))}
                </div>
              )}
              <div style={{ ...sub, marginTop: 10 }}>
                {upfrontInMonth
                  ? <>Unblended is {money(c.unblended - c.amortized)} higher than Amortized this month — an upfront fee was charged and is spread across the commitment term.</>
                  : <>Unblended counts upfront fees in the purchase month; Amortized spreads them across the commitment term.</>}
                {data.reservations && (data.reservations.pct > 0 || (data.riList || []).length > 0) && <> Reserved Instances: utilization {pct(data.reservations.pct)}, net savings {money(data.reservations.netSavings)}.</>}
                {' '}Period {data.start} – {data.end}{data.cached ? ' (cached)' : ''}. SP data can lag up to 48 hours.
              </div>

              <Insights data={data} />
              {errs.length > 0 && (
                <div style={{ ...sub, color: '#b54708', marginTop: 6 }}>
                  {errs.map(([k, v]) => <div key={k}>{k}: {v}</div>)}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const th = { textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#667085', padding: '6px 8px', borderBottom: '1px solid #eaecf0', whiteSpace: 'nowrap' };
const td = { fontSize: 12, color: '#344054', padding: '6px 8px', borderBottom: '1px solid #f2f4f7', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
const h4 = { fontSize: 13, fontWeight: 700, color: '#101828', margin: '16px 0 8px' };
const shortSvc = (s) => (s || '').replace('Amazon Elastic Compute Cloud - Compute', 'EC2').replace('Amazon Relational Database Service', 'RDS').replace('Amazon ElastiCache', 'ElastiCache').replace('Amazon ', '');

function Insights({ data }) {
  const ris = data.riList || [];
  const od = data.onDemand || [];
  const rec = data.recommendation;
  const rc = data.riCoverage;
  const hasSP = !!data.utilization;
  const odTotal = od.reduce((a, x) => a + x.cost, 0);
  const notes = [];
  if (!hasSP) {
    if (ris.length) notes.push('No Savings Plan exists on this account. Steady workloads are committed with Reserved Instances instead (' + ris.length + ' active subscription' + (ris.length > 1 ? 's' : '') + '), so SP Utilization is empty and SP Coverage is 0% by design — RIs are not counted as SP coverage.');
    else notes.push('No Savings Plan or Reserved Instance on this account in this period.');
  }
  if (rc) notes.push('RI coverage: ' + pct(rc.pct) + ' of running instance hours are covered by RIs; the rest (' + Math.round(rc.onDemandHours) + ' h) ran On-Demand.');
  if (odTotal > 0) notes.push('On-Demand spend not covered by any commitment: ' + money(odTotal) + ' this period — see the breakdown below.');
  if (rec && rec.hourly > 0) notes.push('AWS recommends a ' + rec.type + ' at ' + money(rec.hourly) + '/hour, estimated saving ' + money(rec.monthlySavings) + '/month (' + pct(rec.savingsPct) + ').');
  else if (rec) notes.push('AWS has no Savings Plan purchase recommendation for the last 30 days — remaining On-Demand usage is too small or irregular to commit.');
  const soon = ris.filter((r) => r.end && (new Date(r.end) - Date.now()) / 864e5 < 90 && new Date(r.end) > Date.now());
  if (soon.length) notes.push(soon.length + ' RI subscription(s) expire within 90 days — decide whether to renew as RI or replace with a Savings Plan.');

  if (!notes.length && !ris.length && !od.length) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ ...card, background: '#f0f9ff', borderColor: '#b9e6fe' }}>
        <div style={{ ...lbl, color: '#026aa2' }}>Analysis</div>
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {notes.map((n, i) => <li key={i} style={{ fontSize: 12.5, color: '#344054', lineHeight: 1.5 }}>{n}</li>)}
        </ul>
      </div>
      {ris.length > 0 && (
        <>
          <div style={h4}>Reserved Instances ({ris.length})</div>
          <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #eaecf0', borderRadius: 10 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr>{['Instance type', 'Qty', 'Platform', 'Region', 'Offering', 'Expires', 'Utilization', 'Net savings', 'Cost'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {ris.map((r) => (
                  <tr key={r.id}>
                    <td style={td}>{r.instanceType || '–'}</td>
                    <td style={td}>{r.count || '–'}</td>
                    <td style={td}>{r.platform || '–'}</td>
                    <td style={td}>{r.region || '–'}</td>
                    <td style={td}>{r.offering || '–'}</td>
                    <td style={td}>{r.end ? r.end.slice(0, 10) : '–'}</td>
                    <td style={{ ...td, color: tone(r.pct).fg, fontWeight: 600 }}>{pct(r.pct)}</td>
                    <td style={{ ...td, color: r.netSavings > 0 ? '#067647' : '#344054' }}>{money(r.netSavings)}</td>
                    <td style={td}>{money(r.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {od.length > 0 && (
        <>
          <div style={h4}>On-Demand usage not covered ({money(odTotal)})</div>
          <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #eaecf0', borderRadius: 10 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr>{['Service', 'Instance type', 'Hours', 'Cost', 'Share'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {od.map((x, i) => (
                  <tr key={i}>
                    <td style={td}>{shortSvc(x.service)}</td>
                    <td style={td}>{x.instanceType && x.instanceType !== 'NoInstanceType' ? x.instanceType : '–'}</td>
                    <td style={td}>{Math.round(x.hours).toLocaleString('en-US')}</td>
                    <td style={td}>{money(x.cost)}</td>
                    <td style={td}>{odTotal ? pct((x.cost / odTotal) * 100) : '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
